/**
 * The Builds tab of Manage Insights: one page that answers "which games have
 * a report, and what is building right now."
 *
 *  - Coverage grid (top): every game in the picker as a row, every buildable
 *    pack as a column, each cell showing the report state — ready, building,
 *    not built yet, failed, or not available (the game is not served, so no
 *    pack can read it). GET /api/v1/insights/coverage, one read.
 *  - Build queue (below): the insight_runs ledger, the insights twin of the
 *    Layers "Builds" board — what is queued, what is running and for how
 *    long, and what finished last. GET /api/v1/insights/runs.
 *
 * Both re-read every 5s while the tab is visible, so a build-on-all-games
 * kicked off from the registry is watched here without a refresh.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { ClubLogo } from '../components/ClubLogo'
import { pathSeg } from '../lib/urls'
import {
  publishSummary, publishWhenBuilt, REBUILD_SCOPES, type RebuildScope,
} from '../packs/rebuild'
import {
  buildClubPacks, getCoverage, listRuns, publishClubGreen, unpublishClub,
  type Coverage, type CoverageGame, type LabRun,
} from '../packs/promotions'

import './insights-builds.css'

const POLL_MS = 5000

type CellState = 'ok' | 'older' | 'live' | 'candidate' | 'err' | 'na'

const STATE_META: Record<CellState, { glyph: string; label: string }> = {
  ok: { glyph: '●', label: 'report ready' },
  older: { glyph: '●', label: 'ready on an older version' },
  live: { glyph: '◐', label: 'building' },
  candidate: { glyph: '○', label: 'not built yet' },
  err: { glyph: '✕', label: 'build failed' },
  na: { glyph: '–', label: 'not available — game is not served' },
}
const STATES: CellState[] = ['ok', 'older', 'live', 'candidate', 'err', 'na']

type PackCol = Coverage['packs'][number]

/** The publisher's version order (app/insight_packs/marketplace.py
 *  version_key): numeric by dot part (1.3 < 1.6 < 1.10), a pre-release
 *  below its release, trailing zeros equal. null: no numeric order. */
export function versionKey(v: string): [number[], number, string] | null {
  const m = /^(\d+(?:\.\d+)*)(?:-([0-9A-Za-z.-]+))?$/.exec(v)
  if (!m) return null
  const nums = m[1]!.split('.').map(Number)
  while (nums.length > 1 && nums[nums.length - 1] === 0) nums.pop()
  return [nums, m[2] ? 0 : 1, m[2] ?? '']
}

export function compareVersions(a: string, b: string): number {
  const ka = versionKey(a), kb = versionKey(b)
  if (!ka || !kb) return ka ? 1 : kb ? -1 : a.localeCompare(b)
  for (let i = 0; i < Math.max(ka[0].length, kb[0].length); i++) {
    const d = (ka[0][i] ?? 0) - (kb[0][i] ?? 0)
    if (d) return d
  }
  return ka[1] - kb[1] || ka[2].localeCompare(kb[2])
}

/** One column per pack: its latest registered version, with the older
 *  ones it hides (newest first) — so the grid shows what a club should
 *  have, not every version ever uploaded (Ben, 2026-10-22). */
export function latestPacks<P extends { name: string; version: string }>(
  packs: P[],
): (P & { older: string[] })[] {
  const byName = new Map<string, P[]>()
  for (const p of packs) byName.set(p.name, [...(byName.get(p.name) ?? []), p])
  return [...byName.values()].map((vs) => {
    const sorted = [...vs].sort((a, b) => compareVersions(b.version, a.version))
    return { ...sorted[0]!, older: sorted.slice(1).map((p) => p.version) }
  })
}

/** A pack column's hover: what the pack is (its title and description),
 *  the version shown, and the older versions it hides. The header itself
 *  is only the vertical name (Ben, 2026-10-23: thin columns). */
export function packHover(p: PackCol & { older: string[] }): string {
  const lines = [p.title && p.title !== p.name ? `${p.title} (${p.name})` : p.name]
  if (p.description) lines.push(p.description)
  lines.push(p.older.length
    ? `Showing ${p.version}, the latest of ${p.older.length + 1} versions (older: ${p.older.join(', ')})`
    : `Version ${p.version}`)
  return lines.join('\n')
}

type CellView = { state: CellState; version: string }

/** A cell for the pack's LATEST version; when that has no run, a ready
 *  report on an older version shows blue rather than "not built". */
export function cellView(game: CoverageGame, pack: PackCol & { older: string[] },
                         cells: Coverage['cells']): CellView {
  const cell = cells[`${game.game_id}:${pack.name}@${pack.version}`]
  if (cell) {
    if (cell.status === 'succeeded') return { state: 'ok', version: pack.version }
    if (cell.status === 'failed') return { state: 'err', version: pack.version }
    return { state: 'live', version: pack.version } // queued | running
  }
  const older = pack.older.find((v) => cells[`${game.game_id}:${pack.name}@${v}`]?.status === 'succeeded')
  if (older) return { state: 'older', version: older }
  return { state: game.served ? 'candidate' : 'na', version: pack.version }
}

/** "3m 20s", "12s" — a coarse elapsed for the queue, never ms. */
function elapsed(fromISO: string | null): string {
  if (!fromISO) return ''
  const s = Math.max(0, Math.round((Date.now() - Date.parse(fromISO)) / 1000))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  return m < 60 ? `${m}m ${s % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`
}

export default function LabBuilds() {
  const [cov, setCov] = useState<Coverage | null>(null)
  const [runs, setRuns] = useState<LabRun[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  // Clicking a club narrows the page — grid AND queue — to that club, and
  // the selection lives in the URL (?club=<org_code>) so it deep-links and
  // the back button leaves it.
  const [params, setParams] = useSearchParams()
  const clubParam = params.get('club')
  const pickClub = (code: string | null) =>
    setParams(code ? { club: code } : {}, { replace: false })
  // A tick that advances every poll so "building for 20s" counts up live.
  const [, setTick] = useState(0)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = useCallback(() => {
    Promise.all([getCoverage(), listRuns()])
      .then(([c, r]) => { setCov(c); setRuns(r.runs); setError(null) })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
  }, [])

  useEffect(() => {
    load()
    const start = () => {
      if (timer.current) return
      timer.current = setInterval(() => { setTick((t) => t + 1); load() }, POLL_MS)
    }
    const stop = () => {
      if (timer.current) { clearInterval(timer.current); timer.current = null }
    }
    const onVis = () => (document.visibilityState === 'visible' ? start() : stop())
    start()
    document.addEventListener('visibilitychange', onVis)
    return () => { stop(); document.removeEventListener('visibilitychange', onVis) }
  }, [load])

  // One column per pack, its latest version.
  const cols = useMemo(() => (cov ? latestPacks(cov.packs) : []), [cov])

  // Rows grouped by club, narrowed to the picked club (?club=) and then by
  // the free-text box (club or game code).
  const clubs = useMemo(() => {
    if (!cov) return []
    const q = filter.trim().toLowerCase()
    const match = (g: CoverageGame) => !q
      || g.game_code.toLowerCase().includes(q)
      || g.org_code.toLowerCase().includes(q)
      || g.org_name.toLowerCase().includes(q)
    const by = new Map<string, {
      org_id: number; org_code: string; org_name: string
      logo_url: string | null; games: CoverageGame[]
    }>()
    for (const g of cov.games) {
      if (clubParam && g.org_code !== clubParam) continue
      if (!match(g)) continue
      const e = by.get(g.org_code)
        ?? { org_id: g.org_id, org_code: g.org_code, org_name: g.org_name,
             logo_url: g.org_logo_url, games: [] }
      e.games.push(g)
      by.set(g.org_code, e)
    }
    return [...by.values()].sort((a, b) => a.org_code.localeCompare(b.org_code))
  }, [cov, filter, clubParam])

  const scoped = useMemo(
    () => (runs ?? []).filter((r) => !clubParam || r.org_code === clubParam),
    [runs, clubParam])
  const active = useMemo(
    () => scoped
      .filter((r) => r.status === 'queued' || r.status === 'running')
      .sort((a, b) => (a.started_at ?? '').localeCompare(b.started_at ?? '')),
    [scoped])
  const recent = useMemo(
    () => scoped
      .filter((r) => r.status === 'succeeded' || r.status === 'failed')
      .sort((a, b) => (b.finished_at ?? b.computed_at ?? '')
        .localeCompare(a.finished_at ?? a.computed_at ?? ''))
      .slice(0, 12),
    [scoped])
  const running = active.filter((r) => r.status === 'running').length
  const queued = active.length - running

  if (error) return <div className="panel error">Could not load builds — {error}</div>
  if (!cov || !runs) return <p className="muted">Loading…</p>

  return (
    <div className="ib">
      {/* ── live build queue ─────────────────────────────────────────── */}
      <section className="ib-queue">
        <div className="ib-queue-head">
          <h2>Build queue{clubParam ? ` · ${clubParam}` : ''}</h2>
          {clubParam && (
            <button type="button" className="ib-allclubs"
                    onClick={() => pickClub(null)}>
              ← All clubs
            </button>
          )}
          <div className="ib-strip">
            <span className="ib-stat is-live">{running} building</span>
            <span className="ib-stat is-queued">{queued} queued</span>
          </div>
        </div>
        {active.length === 0 ? (
          <p className="muted">Nothing building. Start one from Manage insight
            packs → “build on all games”.</p>
        ) : (
          <table className="ib-runs">
            <thead>
              <tr><th>Pack</th><th>Club</th><th>Game</th><th>State</th><th>For</th></tr>
            </thead>
            <tbody>
              {active.map((r) => (
                <tr key={r.id}>
                  <td>{r.pack_id}@{r.pack_version}</td>
                  <td>{r.org_code}</td>
                  <td>{r.game_code}</td>
                  <td className={r.status === 'running' ? 'is-live' : 'is-queued'}>
                    {r.status}</td>
                  <td>{r.status === 'running' ? elapsed(r.started_at) : 'waiting'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {recent.length > 0 && (
          <details className="ib-recent">
            <summary>Last {recent.length} finished</summary>
            <table className="ib-runs">
              <tbody>
                {recent.map((r) => (
                  <tr key={r.id}>
                    <td>{r.pack_id}@{r.pack_version}</td>
                    <td>{r.org_code}</td>
                    <td>{r.game_code}</td>
                    <td className={r.status === 'failed' ? 'is-err' : 'is-ok'}>
                      {r.status}</td>
                    <td className="ib-err">{r.status === 'failed' ? r.error : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </section>

      {/* ── coverage grid ────────────────────────────────────────────── */}
      <section className="ib-coverage">
        <div className="ib-cov-head">
          <h2>Report coverage</h2>
          <input className="ib-filter" placeholder="Filter clubs or games"
                 value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
        <div className="ib-legend">
          {STATES.map((s) => (
            <span key={s} className={`ib-leg ib-${s}`}>
              <span className="ib-glyph">{STATE_META[s].glyph}</span>
              {STATE_META[s].label}
            </span>
          ))}
        </div>
        {cols.length === 0 ? (
          <p className="muted">No buildable packs registered yet.</p>
        ) : (
          <div className="ib-grid-wrap">
            <table className="ib-grid">
              <thead>
                <tr>
                  <th className="ib-rowhead">Game</th>
                  {cols.map((p) => (
                    <th key={p.name} className="ib-packhead" title={packHover(p)}>
                      <span className="ib-vert">
                        {p.name}<span className="ib-ver">@{p.version}</span>
                      </span>
                    </th>
                  ))}
                  <th className="ib-actions-head" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {clubs.map((club) => (
                  <ClubRows key={club.org_code} club={club} packs={cols}
                            cells={cov.cells} reload={load}
                            picked={clubParam === club.org_code}
                            onPick={pickClub} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function ClubRows({ club, packs, cells, reload, picked, onPick }: {
  club: { org_id: number; org_code: string; org_name: string
          logo_url: string | null; games: CoverageGame[] }
  /** The latest version of each pack (latestPacks). */
  packs: (PackCol & { older: string[] })[]
  cells: Coverage['cells']
  reload: () => void
  picked: boolean
  onPick: (code: string | null) => void
}) {
  // Run one pack — or all — on this club's served games (founder ask,
  // 2026-10-20). The backend queues per pack and starts the builders;
  // progress lands in the Build queue above via the 5s poll.
  const [sel, setSel] = useState('all')
  const [busy, setBusy] = useState(false)
  // which games Publish green reaches: all of the club's, or one
  const [game, setGame] = useState('')
  const selected = () => {
    const [selName = '', ...selVer] = sel.split('@')
    return sel === 'all'
      ? packs.map((p) => ({ name: p.name, version: p.version }))
      : [{ name: selName, version: selVer.join('@') }]
  }
  // Publish every green report — the grid's green: a succeeded build at
  // the pack's latest version (Ben, 2026-10-26). The server skips a page
  // built before the game's serve tables changed, as the single publish
  // does, and says how many.
  const publish = async () => {
    const what = sel === 'all' ? `the latest of all ${packs.length} packs` : sel
    const where = game || `every ${club.org_code} game`
    const reason = window.prompt(
      `Publish ${what}, where green, on ${where} to ${club.org_code}? Reason:`,
      'publish green reports')
    if (reason == null) return
    setBusy(true)
    try {
      const r = await publishClubGreen(club.org_id, selected(), game || null,
                                       reason.trim() || 'publish green reports')
      window.alert(`${club.org_code}: ${publishSummary(r)}`)
      reload()
    } catch (e) {
      window.alert(`Could not publish — ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }
  // Take reports back from the club: every published version of the
  // chosen pack (or all packs), on the chosen game or all of them (Ben,
  // 2026-11-03). The pages stay built and can be published again.
  const unpublish = async () => {
    const what = sel === 'all' ? `all ${packs.length} reports`
      : `the ${sel.split('@')[0]} report (every version)`
    const where = game || `every ${club.org_code} game`
    const reason = window.prompt(
      `Unpublish ${what} on ${where}? ${club.org_code} will stop seeing it. Reason:`,
      'unpublish reports')
    if (reason == null) return
    setBusy(true)
    try {
      const r = await unpublishClub(club.org_id, selected(), game || null,
                                    reason.trim() || 'unpublish reports')
      window.alert(`${club.org_code}: unpublished ${r.unpublished} `
        + `report${r.unpublished === 1 ? '' : 's'}`)
      reload()
    } catch (e) {
      window.alert(`Could not unpublish — ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  // Rebuild in a scope (rebuild.ts), and optionally publish what comes
  // out green once the builds finish (Ben, 2026-10-29).
  const [scope, setScope] = useState<RebuildScope>('latest')
  // builds left while Rebuild & publish waits; null when not waiting
  const [waiting, setWaiting] = useState<number | null>(null)
  const run = async (andPublish: boolean) => {
    // "All packs" at latest/failed is every pack's LATEST version, sent by
    // name: the server's 'all' rebuilds every version ever registered,
    // which is what the "All versions" scope asks for.
    const what = sel === 'all' && scope === 'all' ? 'all' as const : selected()
    const force = scope !== 'failed'
    const scopeLabel = REBUILD_SCOPES.find((s) => s.value === scope)!.label.toLowerCase()
    const label = sel === 'all' ? `all ${packs.length} packs (${scopeLabel})`
      : `${sel} (${scopeLabel})`
    let reason = ''
    if (andPublish) {
      const r = window.prompt(
        `Rebuild ${label} on every served ${club.org_code} game, then publish `
        + `the green reports to ${club.org_code}? Reason:`, 'rebuild and publish')
      if (r == null) return
      reason = r.trim() || 'rebuild and publish'
    } else if (!window.confirm(`Rebuild ${label} on every served ${club.org_code} game?`)) {
      return
    }
    setBusy(true)
    try {
      // "Failed only" on the club row: just the games whose last build
      // failed (Ben, 2026-10-30 — it queued every report)
      const res = await buildClubPacks(club.org_id, what, force, scope === 'failed')
      const queued = res.packs.reduce((n, p) => n + p.queued, 0)
      const errs = res.packs.filter((p) => p.error)
      const started = `${club.org_code}: queued ${queued} build${queued === 1 ? '' : 's'}`
        + (errs.length ? `; ${errs.length} pack(s) could not start: `
          + errs.map((p) => `${p.name}@${p.version} — ${p.error}`).join('; ') : '')
      reload()
      if (!andPublish) { window.alert(started); return }
      setWaiting(queued)
      // Publishes the latest versions only, even after "All versions":
      // a club offered two editions of one report cannot tell which to trust.
      const r = await publishWhenBuilt({
        orgId: club.org_id, gameCode: null,
        packs: sel === 'all' ? packs.map((p) => ({ name: p.name, version: p.version }))
          : selected(),
        reason, onProgress: setWaiting,
      })
      window.alert(`${started}. Builds finished — ${publishSummary(r)}`)
      reload()
    } catch (e) {
      window.alert(`Could not ${andPublish ? 'rebuild and publish' : 'start builds'} — `
        + `${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
      setWaiting(null)
    }
  }
  return (
    <>
      <tr className="ib-club">
        {/* The club's name spans the game and pack columns only; its
            actions sit in their own column to the right of the matrix. As
            one flexed header cell they landed in the Game column and
            widened it to the whole action bar, pushing the matrix off to
            the right (2026-11-03). */}
        <th colSpan={packs.length + 1}>
          {/* Clicking the club narrows the page to it (and the URL carries
              it); clicking again widens back out. */}
          <button type="button" className="ib-club-name"
                  title={picked ? 'Show all clubs'
                    : `Show only ${club.org_code}'s games and builds`}
                  onClick={() => onPick(picked ? null : club.org_code)}>
            <ClubLogo code={club.org_code} url={club.logo_url}
                      name={club.org_name} size="sm" />
            {club.org_name} <span className="muted">({club.org_code})</span>
          </button>
        </th>
        <td className="ib-actions" rowSpan={club.games.length + 1}>
          <div className="ib-act-group" role="group" aria-label={`Build ${club.org_code}`}>
            <span className="ib-act-label">Build</span>
            <select value={sel} onChange={(e) => setSel(e.target.value)}
                    disabled={busy} aria-label={`Pack to run for ${club.org_code}`}>
              <option value="all">All packs (latest)</option>
              {packs.map((p) => (
                <option key={`${p.name}@${p.version}`} value={`${p.name}@${p.version}`}>
                  {p.name}@{p.version}
                </option>
              ))}
            </select>
            <select value={scope} onChange={(e) => setScope(e.target.value as RebuildScope)}
                    disabled={busy} aria-label={`What to rebuild for ${club.org_code}`}>
              {REBUILD_SCOPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <div className="ib-act-buttons">
              <button type="button" disabled={busy} onClick={() => void run(false)}
                      title={`Build on every served ${club.org_code} game`}>
                {busy && waiting === null ? 'Starting…' : 'Rebuild'}
              </button>
              <button type="button" className="is-primary" disabled={busy}
                      onClick={() => void run(true)}
                      title={`Build on every served ${club.org_code} game, then publish `
                        + 'the green reports when the builds finish (keep this tab open)'}>
                {waiting === null ? 'Rebuild & publish'
                  : `Publishing when built (${waiting} left)…`}
              </button>
            </div>
          </div>
          <div className="ib-act-group" role="group" aria-label={`Publish ${club.org_code}`}>
            <span className="ib-act-label">Publish</span>
            <select value={game} onChange={(e) => setGame(e.target.value)}
                    disabled={busy} aria-label={`Games to publish for ${club.org_code}`}>
              <option value="">All games</option>
              {club.games.map((g) => (
                <option key={g.game_id} value={g.game_code}>only {g.game_code}</option>
              ))}
            </select>
            <div className="ib-act-buttons">
              <button type="button" disabled={busy} onClick={() => void publish()}
                      title={`Publish every green report to ${club.org_code}`}>
                Publish green
              </button>
              <button type="button" disabled={busy} onClick={() => void unpublish()}
                      title={`Take the chosen reports back from ${club.org_code}`}>
                Unpublish
              </button>
            </div>
          </div>
        </td>
      </tr>
      {club.games.map((g) => (
        <tr key={g.game_id}>
          <td className="ib-rowhead">
            <Link to={`/manage-insights/game/${pathSeg(g.game_code)}`}>{g.game_code}</Link>
            {g.match_date && <span className="ib-date">{g.match_date}</span>}
          </td>
          {packs.map((p) => {
            const { state: st, version } = cellView(g, p, cells)
            const meta = STATE_META[st]
            const title = st === 'older'
              ? `${p.name}@${version} ready — latest ${p.version} not built on this game`
              : `${p.name}@${p.version} — ${meta.label}`
            const glyph = <span className="ib-glyph" aria-label={meta.label}>{meta.glyph}</span>
            return (
              <td key={p.name} className={`ib-cell ib-${st}`} title={title}>
                {st === 'ok' || st === 'older'
                  ? <Link to={`/manage-insights/pack/${pathSeg(p.name)}/${pathSeg(version)}/${pathSeg(g.game_code)}`}>{glyph}</Link>
                  : glyph}
              </td>
            )
          })}
        </tr>
      ))}
    </>
  )
}
